import { useCallback, useEffect, useRef, useState } from 'react'

import pluralize from 'pluralize'
import { styled } from 'styled-components'

import { FormattedMessage, Modal } from '@b-stock/fe-core/bstock-react'

import { ModalContent, ModalFooter } from '@/components/common/modal/ui'
import FilterCheckbox from '@/components/listings/search/SearchFilters/shared/FilterCheckbox'
import {
  FilterModalHeader,
  ModalStatistic,
  TotalSelected,
} from '@/components/listings/search/SearchFilters/shared/Modal'
import OnlyableCheckbox from '@/components/listings/search/SearchFilters/shared/OnlyableCheckbox'
import {
  ModalPanelArea,
  ModalSectionHeader,
  ModalSummaryFooter,
} from '@/components/listings/search/SearchFilters/shared/SectionedFilterModal'
import SeeMoreButton from '@/components/listings/search/SearchFilters/shared/SeeMoreButton'

import { useTrackFilterUsage } from './analytics'

type RecordType = {
  displayName: React.ReactNode
  key: string
}

type RecordTypeWithDisable = {
  displayName: React.ReactNode
  key: string
  disabled: boolean
}

type CategoricalFilterProps = {
  items: RecordType[]
  alwaysUseModal?: boolean
  selected: Set<string>
  onChange: (value: Set<string>) => void
  name?: string
  desktopScreen?: boolean
  customModal?: (param: () => void) => React.ReactNode
  sortOrder?: 'row' | 'column'
  // Extra list item(s) appended to the end of the rendered <ol>, so callers can
  // add adjacent toggles (e.g. the Accepts Credit Card checkbox) that live in
  // the same visual list as the categorical options. Must render <li> elements.
  appendItems?: React.ReactNode
  // Optional extras for the desktop modal flow. `renderModalPanel` switches
  // the modal to a sectioned layout: the panel it renders sits above the
  // options, "Select all" moves into a section header titled
  // `modalSectionTitle`, and the footer shows the summary beside the actions,
  // with `renderModalSummaryNote` under it. Both receive the staged selection.
  // `onModalSubmit` / `onModalReset` fire alongside committing or discarding
  // that staged selection, so callers can stage their own state.
  renderModalPanel?: (modalSelected: Set<string>) => React.ReactNode
  modalSectionTitle?: React.ReactNode
  renderModalSummaryNote?: (modalSelected: Set<string>) => React.ReactNode
  onModalSubmit?: () => void
  onModalReset?: () => void
}

const List = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  label > span {
    text-transform: capitalize;
  }
`

const COLUMN_GAP_REM = 3
const COLUMN_MIN_WIDTH_REM = 17.4
const TWO_COLUMN_MIN_WIDTH = `${2 * COLUMN_MIN_WIDTH_REM + COLUMN_GAP_REM}rem`

// The intrinsic size lets a shrink-to-fit parent (a centered modal) still
// grow to fit two columns.
const ModalListContainer = styled.div`
  contain-intrinsic-inline-size: ${TWO_COLUMN_MIN_WIDTH};
  container-type: inline-size;
`

type ModalList = {
  $items: number
  $sortOrder: 'row' | 'column'
}

const ModalList = styled(List)<ModalList>`
  flex-wrap: wrap;
  row-gap: 0.5rem;
  column-gap: ${COLUMN_GAP_REM}rem;
  li {
    position: relative;
  }

  @container (min-width: ${TWO_COLUMN_MIN_WIDTH}) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(${({ $items }) => Math.ceil($items / 2)}, auto);
    grid-auto-flow: ${({ $sortOrder }) => $sortOrder};
    gap: 0.5rem ${COLUMN_GAP_REM}rem;

    li {
      min-width: ${COLUMN_MIN_WIDTH_REM}rem;
    }
  }
`

export const splitItems = (
  items: Array<RecordType>,
  alwaysUseModal?: boolean
): {
  itemsCount: number
  displayMore: Array<RecordTypeWithDisable>
  displayItems: Array<RecordTypeWithDisable>
  displayModal: boolean
} => {
  const itemsCount = items.length
  let displayMore: Array<RecordTypeWithDisable> = []
  let displayItems: Array<RecordTypeWithDisable> = []

  const displayModal = alwaysUseModal || itemsCount > 16
  if (itemsCount <= 8) {
    displayItems = items
      .slice(0, 5)
      .map((item) => ({ ...item, disabled: false }))
    displayMore = items
      .slice(5, 8)
      .map((item) => ({ ...item, disabled: false }))
  } else if (itemsCount > 8 && itemsCount <= 16) {
    displayItems = items
      .slice(0, 5)
      .map((item) => ({ ...item, disabled: false }))
    displayMore = items
      .slice(5, 16)
      .map((item) => ({ ...item, disabled: false }))
  } else if (itemsCount > 16) {
    displayItems = items
      .slice(0, 5)
      .map((item) => ({ ...item, disabled: false }))
  }

  return {
    itemsCount,
    displayMore,
    displayItems,
    displayModal,
  }
}

type TrackFilterClick = (args: {
  device: 'Mobile' | 'Desktop'
  filter_name: string
  is_selected: boolean
}) => void

interface DesktopCategoricalFilterProps extends Pick<
  CategoricalFilterProps,
  'items' | 'selected' | 'customModal'
> {
  alwaysUseModal?: boolean
  handleOnlyClick: (key: string) => void
  handleModalOnlyClick: (key: string) => void
  filterTitle: string
  handleModalChange: (key: string) => void
  handleChange: (key: string) => void
  resetFilters: () => void
  submitModal: () => void
  modalSelected: Set<string>
  trackFilterClick: TrackFilterClick
  sortOrder?: 'row' | 'column'
  handleSelectAll: () => void
  handleModalShiftRange: (anchorKey: string, clickedKey: string) => void
  appendItems?: React.ReactNode
  renderModalPanel?: CategoricalFilterProps['renderModalPanel']
  modalSectionTitle?: CategoricalFilterProps['modalSectionTitle']
  renderModalSummaryNote?: CategoricalFilterProps['renderModalSummaryNote']
}

interface MobileCategoricalFilterProps extends Pick<
  CategoricalFilterProps,
  'items' | 'selected'
> {
  handleOnlyClick: (key: string) => void
  handleChange: (key: string) => void
  handleShiftRange: (anchorKey: string, clickedKey: string) => void
  trackFilterClick: TrackFilterClick
  sortOrder?: 'row' | 'column'
  appendItems?: React.ReactNode
}

const DesktopCategoricalFilter = ({
  items,
  alwaysUseModal,
  selected,
  modalSelected,
  customModal,
  handleOnlyClick,
  handleModalOnlyClick,
  handleChange,
  handleModalChange,
  filterTitle,
  submitModal,
  resetFilters,
  trackFilterClick,
  sortOrder,
  handleSelectAll,
  handleModalShiftRange,
  appendItems,
  renderModalPanel,
  modalSectionTitle,
  renderModalSummaryNote,
}: DesktopCategoricalFilterProps) => {
  const [showMore, setShowMore] = useState<boolean>(false)
  const [showModal, setShowModal] = useState<boolean>(false)
  const lastClickedKeyRef = useRef<string | null>(null)
  const suppressNextChangeRef = useRef(false)

  const selectedCount = items.filter((item) =>
    modalSelected.has(item.key)
  ).length
  const allSelected = items.length > 0 && selectedCount === items.length
  const someSelected = selectedCount > 0 && !allSelected
  const { displayMore, displayItems, displayModal } = splitItems(
    items,
    alwaysUseModal
  )

  const handleShowMore = () => {
    if (!showMore) {
      setShowModal(true)
    }
    setShowMore((showMore) => !showMore)
  }

  const cancelShowMore = () => {
    resetFilters()
    setShowMore(false)
    lastClickedKeyRef.current = null
    suppressNextChangeRef.current = false
  }

  const handleModalClose = () => {
    resetFilters()
    setShowMore(false)
    lastClickedKeyRef.current = null
    suppressNextChangeRef.current = false
  }

  const handleFilterClick = (name: string, checked: boolean) => {
    trackFilterClick({
      device: 'Desktop',
      filter_name: name,
      is_selected: !checked, // User is clicking on an unselected checkbox to flip the state to selected
    })
  }

  const selectAll = (
    <FilterCheckbox
      checked={allSelected ? true : someSelected ? 'PARTIAL' : false}
      label={<FormattedMessage id="Filters.modal.selectAll" />}
      onChange={handleSelectAll}
    />
  )

  const selectedSummary = (
    <FormattedMessage
      id="Filters.modal.list"
      values={{
        amount: modalSelected.size,
        name: pluralize(filterTitle, modalSelected.size),
      }}
    />
  )

  const handleModalSubmit = () => {
    // Track modal submission with all selected filters
    if (modalSelected.size > 0) {
      const selectedFilterNames = Array.from(modalSelected).map((key) => {
        // Use the key (raw value) instead of trying to extract text from JSX displayName
        return key
      })

      trackFilterClick({
        device: 'Desktop',
        filter_name: selectedFilterNames.join(', '),
        is_selected: true,
      })
    }

    submitModal()
    cancelShowMore()
  }

  return (
    <>
      <List>
        {displayItems.map(({ displayName, key }) => (
          <OnlyableCheckbox
            key={key}
            displayName={displayName}
            checked={selected.has(key)}
            onChange={() => handleChange(key)}
            onOnly={() => handleOnlyClick(key)}
            //disabled={disabled}
            onFilterClick={() => handleFilterClick(key, selected.has(key))}
          />
        ))}

        {showMore &&
          displayMore.map(({ displayName, key }) => (
            <OnlyableCheckbox
              key={key}
              displayName={displayName}
              checked={selected.has(key)}
              onChange={() => handleChange(key)}
              onOnly={() => handleOnlyClick(key)}
              onFilterClick={() => handleFilterClick(key, selected.has(key))}
            />
          ))}
        {items.length > 5 && (
          <SeeMoreButton showMore={showMore} handleShowMore={handleShowMore} />
        )}
        {appendItems}
      </List>
      {displayModal &&
        showMore &&
        showModal &&
        (customModal ? (
          // oxlint-disable-next-line react/refs
          customModal(handleModalClose)
        ) : (
          <Modal closeModal={handleModalClose}>
            {renderModalPanel ? (
              <>
                <FilterModalHeader
                  title={filterTitle}
                  onClose={handleModalClose}
                  divider={false}
                />
                <ModalPanelArea>
                  {renderModalPanel(modalSelected)}
                </ModalPanelArea>
                <ModalSectionHeader title={modalSectionTitle}>
                  {selectAll}
                </ModalSectionHeader>
              </>
            ) : (
              <FilterModalHeader title={filterTitle} onClose={handleModalClose}>
                {selectAll}
              </FilterModalHeader>
            )}
            <ModalContent>
              <ModalListContainer>
                <ModalList
                  $items={items.length}
                  $sortOrder={sortOrder || 'row'}
                >
                  {items.map(({ displayName, key }) => (
                    <OnlyableCheckbox
                      key={key}
                      displayName={displayName}
                      checked={modalSelected.has(key)}
                      onChange={() => {
                        if (suppressNextChangeRef.current) {
                          suppressNextChangeRef.current = false
                          return
                        }
                        handleModalChange(key)
                      }}
                      onOnly={() => handleModalOnlyClick(key)}
                      onClick={(e) => {
                        if (e.shiftKey && lastClickedKeyRef.current !== null) {
                          suppressNextChangeRef.current = true
                          handleModalShiftRange(lastClickedKeyRef.current, key)
                        }
                        lastClickedKeyRef.current = key
                      }}
                    />
                  ))}
                </ModalList>
              </ModalListContainer>
            </ModalContent>
            {renderModalPanel ? (
              <ModalSummaryFooter
                summary={selectedSummary}
                note={renderModalSummaryNote?.(modalSelected)}
                onCancel={cancelShowMore}
                onSubmit={handleModalSubmit}
              />
            ) : (
              <ModalFooter>
                <ModalStatistic>
                  <TotalSelected>{selectedSummary}</TotalSelected>
                </ModalStatistic>
                <Modal.Actions
                  alignment="end"
                  submitMessage={<FormattedMessage id="Common.showResult" />}
                  cancelMessage={<FormattedMessage id="Common.cancel" />}
                  onCancel={cancelShowMore}
                  onSubmit={handleModalSubmit}
                />
              </ModalFooter>
            )}
          </Modal>
        ))}
    </>
  )
}

const MobileCategoricalFilter = ({
  items,
  selected,
  handleChange,
  handleOnlyClick,
  handleShiftRange,
  trackFilterClick,
  sortOrder,
  appendItems,
}: MobileCategoricalFilterProps) => {
  const lastClickedKeyRef = useRef<string | null>(null)
  const suppressNextChangeRef = useRef(false)

  const handleFilterClick = (name: string, checked: boolean) => {
    trackFilterClick({
      device: 'Mobile',
      filter_name: name,
      is_selected: !checked, // User is clicking on an unselected checkbox to flip the state to selected
    })
  }

  return (
    <ModalListContainer>
      <ModalList $items={items.length} $sortOrder={sortOrder || 'row'}>
        {items.map(({ displayName, key }) => (
          <OnlyableCheckbox
            key={key}
            displayName={displayName}
            checked={selected.has(key)}
            onChange={() => {
              if (suppressNextChangeRef.current) {
                suppressNextChangeRef.current = false
                return
              }
              handleChange(key)
            }}
            onOnly={() => handleOnlyClick(key)}
            onFilterClick={() => handleFilterClick(key, selected.has(key))}
            onClick={(e) => {
              if (e.shiftKey && lastClickedKeyRef.current !== null) {
                suppressNextChangeRef.current = true
                handleShiftRange(lastClickedKeyRef.current, key)
              }
              lastClickedKeyRef.current = key
            }}
          />
        ))}
        {appendItems}
      </ModalList>
    </ModalListContainer>
  )
}

const CategoricalFilter = ({
  items,
  alwaysUseModal,
  selected,
  onChange,
  name,
  customModal,
  desktopScreen,
  sortOrder,
  appendItems,
  renderModalPanel,
  modalSectionTitle,
  renderModalSummaryNote,
  onModalSubmit,
  onModalReset,
}: CategoricalFilterProps) => {
  const [updatedFilters, setUpdatedFilter] = useState<Set<string>>(selected)

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setUpdatedFilter(selected)
  }, [selected])

  const handleChange = (key: string) => {
    const clone = new Set([...selected])
    if (clone.has(key)) {
      clone.delete(key)
    } else {
      clone.add(key)
    }
    onChange(clone)
  }

  const handleModalChange = (key: string, fireOnChange = false) => {
    const clone = new Set([...updatedFilters])
    if (clone.has(key)) {
      clone.delete(key)
    } else {
      clone.add(key)
    }
    setUpdatedFilter(clone)

    if (fireOnChange) {
      onChange(clone)
    }
  }

  // Mobile taps stage against `updatedFilters` (local state) rather than the
  // committed `selected` prop, then fire `onChange` immediately. Reading
  // `selected` here meant each tap derived from the last committed value, which
  // lags a tap behind while the parent round-trips the update — so rapid
  // multi-selects dropped or double-toggled entries. Staging locally keeps the
  // rendered checkbox and successive taps consistent within the same render.
  const handleMobileChange = (key: string) => {
    handleModalChange(key, true)
  }

  const submitModal = () => {
    onModalSubmit?.()
    onChange(updatedFilters)
  }

  const handleOnlyClick = (key: string) => {
    onChange(new Set([key]))
  }

  const handleModalOnlyClick = (key: string, fireOnChange = false) => {
    const newValue = new Set([key])
    setUpdatedFilter(newValue)

    if (fireOnChange) {
      onChange(newValue)
    }
  }

  const handleMobileOnlyClick = (key: string) => {
    handleModalOnlyClick(key, true)
  }

  const resetFilters = () => {
    setUpdatedFilter(selected)
    onModalReset?.()
  }

  // Same staging model as handleMobileChange: derive the range from the local
  // `updatedFilters`, commit it back with setUpdatedFilter, then fire onChange.
  const handleMobileShiftRange = (anchorKey: string, clickedKey: string) => {
    const targetState = !updatedFilters.has(clickedKey)
    const fromIdx = items.findIndex((i) => i.key === anchorKey)
    const toIdx = items.findIndex((i) => i.key === clickedKey)

    if (fromIdx === -1 || toIdx === -1) return

    const isColumnOrder = (sortOrder ?? 'row') === 'column'
    const nPerCol = Math.ceil(items.length / 2)
    const getGridPos = (idx: number) =>
      isColumnOrder
        ? {
            col: idx < nPerCol ? 0 : 1,
            row: idx < nPerCol ? idx : idx - nPerCol,
          }
        : { col: idx % 2, row: Math.floor(idx / 2) }

    const from = getGridPos(fromIdx)
    const to = getGridPos(toIdx)
    const minRow = Math.min(from.row, to.row)
    const maxRow = Math.max(from.row, to.row)
    const minCol = Math.min(from.col, to.col)
    const maxCol = Math.max(from.col, to.col)

    const clone = new Set([...updatedFilters])
    items.forEach((item, i) => {
      const { row, col } = getGridPos(i)
      if (row >= minRow && row <= maxRow && col >= minCol && col <= maxCol) {
        if (targetState) {
          clone.add(item.key)
        } else {
          clone.delete(item.key)
        }
      }
    })
    setUpdatedFilter(clone)
    onChange(clone)
  }

  const handleSelectAll = () => {
    const selectedCount = items.filter((i) => updatedFilters.has(i.key)).length
    if (selectedCount === items.length && items.length > 0) {
      setUpdatedFilter(new Set())
    } else {
      setUpdatedFilter(new Set(items.map((i) => i.key)))
    }
  }

  const handleModalShiftRange = (anchorKey: string, clickedKey: string) => {
    const targetState = !updatedFilters.has(clickedKey)
    const fromIdx = items.findIndex((i) => i.key === anchorKey)
    const toIdx = items.findIndex((i) => i.key === clickedKey)

    if (fromIdx === -1 || toIdx === -1) return

    const isColumnOrder = (sortOrder ?? 'row') === 'column'
    const nPerCol = Math.ceil(items.length / 2)
    const getGridPos = (idx: number) =>
      isColumnOrder
        ? {
            col: idx < nPerCol ? 0 : 1,
            row: idx < nPerCol ? idx : idx - nPerCol,
          }
        : { col: idx % 2, row: Math.floor(idx / 2) }

    const from = getGridPos(fromIdx)
    const to = getGridPos(toIdx)
    const minRow = Math.min(from.row, to.row)
    const maxRow = Math.max(from.row, to.row)
    const minCol = Math.min(from.col, to.col)
    const maxCol = Math.max(from.col, to.col)

    const clone = new Set([...updatedFilters])
    items.forEach((item, i) => {
      const { row, col } = getGridPos(i)
      if (row >= minRow && row <= maxRow && col >= minCol && col <= maxCol) {
        if (targetState) {
          clone.add(item.key)
        } else {
          clone.delete(item.key)
        }
      }
    })
    setUpdatedFilter(clone)
  }

  const filterTitle = name ? name.replace(/filter/gi, '').toLowerCase() : ''

  const trackFilterUsage = useTrackFilterUsage<{
    device: string
    filter_title: string
    filter_name: string
    is_selected: boolean
    alphabetical_filter: boolean
  }>('filters')
  const trackFilterClick = useCallback<TrackFilterClick>(
    ({ device, filter_name, is_selected }) => {
      trackFilterUsage({
        alphabetical_filter: false,
        device,
        filter_title: filterTitle,
        filter_name,
        is_selected,
      })
    },
    [filterTitle, trackFilterUsage]
  )

  return desktopScreen ? (
    <DesktopCategoricalFilter
      items={items}
      alwaysUseModal={alwaysUseModal}
      selected={selected}
      modalSelected={updatedFilters}
      resetFilters={resetFilters}
      customModal={customModal}
      filterTitle={filterTitle}
      handleOnlyClick={handleOnlyClick}
      handleModalOnlyClick={handleModalOnlyClick}
      handleChange={handleChange}
      handleModalChange={handleModalChange}
      submitModal={submitModal}
      trackFilterClick={trackFilterClick}
      sortOrder={sortOrder}
      handleSelectAll={handleSelectAll}
      handleModalShiftRange={handleModalShiftRange}
      appendItems={appendItems}
      renderModalPanel={renderModalPanel}
      modalSectionTitle={modalSectionTitle}
      renderModalSummaryNote={renderModalSummaryNote}
    />
  ) : (
    <MobileCategoricalFilter
      items={items}
      selected={updatedFilters}
      handleChange={handleMobileChange}
      handleOnlyClick={handleMobileOnlyClick}
      handleShiftRange={handleMobileShiftRange}
      trackFilterClick={trackFilterClick}
      sortOrder={sortOrder}
      appendItems={appendItems}
    />
  )
}

export default CategoricalFilter

export type { RecordType }
